import { fetchPlayers } from '@api-service/players';
import type { Player } from '@api-service/players';
import { chunk, OPTA_IDS_PER_REQUEST } from '@lib/article-blocks/hydrate';

/**
 * What the page knows about a player beyond the lineups read: the headshot,
 * and the facts an FPL manager weighs when they see a name on a team sheet.
 */
export type PlayerFacts = {
  imageUrl: string;
  displayName: string;
  position: Player['position'];
  price: number;
  ownership: number | null;
  status: Player['status'];
  chanceOfPlaying: number | null;
  news: string | null;
};
export type PlayerFactsMap = Record<string, PlayerFacts>;

/**
 * The lineups read carries names and ids only, so the page looks the players
 * up once at build time, fifteen per call like the article hydrator.
 */
export const fetchPlayerFacts = async (
  optaIds: string[]
): Promise<PlayerFactsMap> => {
  if (optaIds.length === 0) return {};
  const pages = await Promise.all(
    chunk(optaIds, OPTA_IDS_PER_REQUEST).map((ids) =>
      fetchPlayers({
        optaIds: ids.join(','),
        exclude: 'predictions',
        limit: OPTA_IDS_PER_REQUEST
      })
    )
  );
  return Object.fromEntries(
    pages
      .flatMap((page) => page.data)
      .map((player) => [
        player.externalIds.optaUuid,
        {
          imageUrl: player.imageUrl,
          displayName: player.displayName,
          position: player.position,
          price: player.price,
          ownership: player.ownership,
          status: player.status,
          chanceOfPlaying: player.chanceOfPlaying,
          news: player.news
        }
      ])
  );
};
